import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

// Import components
import ProjectsPage from '../components/Projects';
import TasksPage from '../components/Tasks';
import ReportsPage from '../components/Reports';
import TeamPage from '../components/Team';
import ProfilePage from '../components/Profile';
import TimeEntriesPage from '../components/TimeEntries';
import InvoicesPage from '../components/Invoices';
import Dashboard from '../components/Dashboard';
import TaskDetail from '../components/TaskDetail';
import ProjectDetail from '../components/ProjectDetail';
import ForgotPassword from '../components/ForgotPassword';
import VerifyEmail from '../components/VerifyEmail';
import CheckEmail from '../components/CheckEmail';
import ResetPassword from '../components/ResetPassword';
import UserList from '../components/UserList';
import UserDetail from '../components/UserDetail';
import AcceptInvite from '../components/AcceptInvite';
import HelpGuidelines from '../components/HelpGuidelines';
import ResourceUtilization from '../components/ResourceUtilization';
import AIValidation from '../components/AIValidation';
import PendingApprovalDashboard from '../components/PendingApprovalDashboard';
import Timesheet from '../components/Timesheet';
import AdminDashboard from '../components/AdminDashboard';
import LeaveManagement from '../components/LeaveManagement';
import LeaveEmailAction from '../components/LeaveEmailAction';
import LeaveBalance from '../components/LeaveBalance';
import AttendanceManagement from '../components/AttendanceManagement';
import TwoFactorVerification from '../components/TwoFactorVerification';
import TwoFactorSetup from '../components/TwoFactorSetup';
import OnboardingFlow from '../components/OnboardingFlow';
import ForceLightMode from '../components/ForceLightMode';
import GitHubCallback from '../components/GitHubCallback';
import GitHubIntegration from '../components/GitHubIntegration';
import OutlookCalendar from '../components/OutlookCalendar';
import SpotifyConnect from '../components/spotify/SpotifyConnect';
import SpotifyCallback from '../components/spotify/SpotifyCallback';
import MicrosoftCallback from '../components/MicrosoftCallback';
import MicrosoftLoginCallback from '../components/MicrosoftLoginCallback';
import MyActivity from '../components/MyActivity';
import TalkToTickSync from '../components/TalkToTickSync';
import AdminChatHistory from '../components/AdminChatHistory';
import AdminMessageLogs from '../components/AdminMessageLogs';
import PromotionalEmails from '../components/PromotionalEmails';
import AuthForm from '../components/AuthForm';
import Layout from '../components/Layout';
import Chat from '../components/Chat';
import Inbox from '../components/Inbox';
import AnnouncementManager from '../components/AnnouncementManager';
import PublicProjectView from '../components/public/PublicProjectView';

// Import route protection components
import { ProtectedRoute, RoleProtectedRoute, PendingApprovalRoute } from './RouteProtection';

// Wrapper components for dynamic routes
function TaskDetailWrapper() {
  const { useParams, useNavigate } = require('react-router-dom');
  const { id } = useParams();
  const navigate = useNavigate();
  
  const handleBack = () => {
    // If there's browser history, go back
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Otherwise, go to tasks list
      navigate(ROUTES.TASKS);
    }
  };
  
  return <TaskDetail taskId={id} onBack={handleBack} />;
}

function ProjectDetailWrapper() {
  const { useParams, useNavigate } = require('react-router-dom');
  const { id } = useParams();
  const navigate = useNavigate();
  
  const handleBack = () => {
    // If there's browser history, go back
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Otherwise, go to projects list
      navigate(ROUTES.PROJECTS);
    }
  };
  
  return <ProjectDetail projectId={id} onBack={handleBack} />;
}

function TwoFactorSetupWrapper() {
  const { useNavigate } = require('react-router-dom');
  const navigate = useNavigate();
  return (
    <ForceLightMode>
      <TwoFactorSetup
        onComplete={() => navigate(ROUTES.PROFILE)}
        onBack={() => navigate(ROUTES.PROFILE)}
        shouldCompleteOnboarding={true}
      />
    </ForceLightMode>
  );
}

function TwoFactorSetupOnboardingWrapper() {
  const { useNavigate } = require('react-router-dom');
  const navigate = useNavigate();
  return (
    <TwoFactorSetup 
      onComplete={() => navigate(ROUTES.ONBOARDING)}
      onBack={() => navigate(ROUTES.ONBOARDING)}
      shouldCompleteOnboarding={false}
    />
  );
}

function TwoFactorVerificationWrapper() {
  const { useNavigate } = require('react-router-dom');
  const { useAuth } = require('../contexts/AuthContext');
  const navigate = useNavigate();
  const { user, pending2FAUser, complete2FALogin } = useAuth();
  const challengeUser = pending2FAUser || user;
  
  const handleVerificationSuccess = async (code) => {
    if (challengeUser && challengeUser.username) {
      const success = await complete2FALogin(challengeUser.username, code);
      if (success) {
        navigate(ROUTES.DASHBOARD);
      }
    }
  };

  const handleBack = () => {
    // Clear user data and redirect to login
    localStorage.removeItem('token');
    navigate(ROUTES.LOGIN);
  };

  const handleCancel = () => {
    // Clear user data and redirect to login
    localStorage.removeItem('token');
    navigate(ROUTES.LOGIN);
  };

  return (
    <ForceLightMode>
      <TwoFactorVerification
        username={challengeUser?.username}
        onSuccess={handleVerificationSuccess}
        onBack={handleBack}
        onCancel={handleCancel}
        hideNavigation={true}
      />
    </ForceLightMode>
  );
}

function OnboardingFlowWrapper() {
  const { useAuth } = require('../contexts/AuthContext');
  const { user, setUser } = useAuth();

  const handleOnboardingComplete = () => {
    window.location.href = ROUTES.DASHBOARD;
  };

  return (
    <OnboardingFlow
      username={user?.username}
      onComplete={handleOnboardingComplete}
      setUser={setUser}
    />
  );
}

// Route configuration
export const routeConfig = [
  // Public routes
  { path: ROUTES.LOGIN, element: <AuthForm isLogin={true} />, public: true },
  { path: ROUTES.REGISTER, element: <AuthForm isLogin={false} />, public: true },
  { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPassword />, public: true },
  { path: ROUTES.VERIFY_EMAIL, element: <VerifyEmail />, public: true },
  { path: ROUTES.CHECK_EMAIL, element: <CheckEmail />, public: true },
  { path: ROUTES.RESET_PASSWORD, element: <ResetPassword />, public: true },
  { path: ROUTES.ACCEPT_INVITE, element: <ForceLightMode><AcceptInvite /></ForceLightMode>, public: true },
  { path: ROUTES.GITHUB_CALLBACK, element: <GitHubCallback />, public: true },
  { path: ROUTES.SPOTIFY_CALLBACK, element: <SpotifyCallback />, public: true },
  { path: ROUTES.MICROSOFT_CALLBACK, element: <MicrosoftCallback />, public: true },
  { path: ROUTES.MICROSOFT_LOGIN_CALLBACK, element: <MicrosoftLoginCallback />, public: true },
  { path: ROUTES.TWO_FACTOR_VERIFICATION, element: <TwoFactorVerificationWrapper />, public: true },
  { path: ROUTES.SHARE, element: <ForceLightMode><PublicProjectView /></ForceLightMode>, public: true },
  { path: ROUTES.LEAVE_EMAIL_ACTION, element: <ForceLightMode><LeaveEmailAction /></ForceLightMode>, public: true },
  
  // Protected routes
  { path: ROUTES.DASHBOARD, element: <Dashboard />, protected: true },
  { path: ROUTES.TIME_ENTRIES, element: <TimeEntriesPage />, protected: true },
  { path: ROUTES.TIMESHEET, element: <Timesheet />, protected: true },
  { path: ROUTES.PROJECTS, element: <ProjectsPage />, protected: true },
  { path: ROUTES.TASKS, element: <TasksPage />, protected: true },
  { path: ROUTES.REPORTS, element: <ReportsPage />, protected: true },
  { path: ROUTES.INVOICES, element: <InvoicesPage />, protected: true },
  { path: ROUTES.TEAM, element: <TeamPage />, protected: true, roles: ['admin', 'project_lead'] },
  { path: ROUTES.MESSAGES, element: <Chat />, protected: true },
  { path: ROUTES.INBOX, element: <Inbox />, protected: true },
  { path: ROUTES.HELP, element: <HelpGuidelines />, protected: true },
  { path: ROUTES.PROFILE, element: <ProfilePage />, protected: true },
  { path: ROUTES.CALENDAR, element: <OutlookCalendar />, protected: true },
  { path: ROUTES.LEAVE_MANAGEMENT, element: <LeaveManagement />, protected: true },
  { path: ROUTES.MY_ACTIVITY, element: <MyActivity />, protected: true },
  { path: ROUTES.TALK_TO_TICK_SYNC, element: <TalkToTickSync />, protected: true },
  { path: ROUTES.GITHUB_INTEGRATION, element: <GitHubIntegration />, protected: true },
  { path: ROUTES.SPOTIFY, element: <SpotifyConnect />, protected: true },
  
  // Dynamic routes
  { path: ROUTES.TASK_DETAIL, element: <TaskDetailWrapper />, protected: true },
  { path: ROUTES.PROJECT_DETAIL, element: <ProjectDetailWrapper />, protected: true },
  
  // Admin routes
  { path: ROUTES.USERS, element: <UserList />, protected: true, roles: ['admin'] },
  { path: ROUTES.USER_DETAIL, element: <UserDetail />, protected: true, roles: ['admin'] },
  { path: ROUTES.RESOURCE_UTILIZATION, element: <ResourceUtilization />, protected: true, roles: ['admin'] },
  { path: ROUTES.RESOURCE_UTIL_PEOPLE, element: <ResourceUtilization />, protected: true, roles: ['admin'] },
  { path: ROUTES.RESOURCE_UTIL_PROJECTS, element: <ResourceUtilization />, protected: true, roles: ['admin'] },
  { path: ROUTES.RESOURCE_UTIL_TIMELINE, element: <ResourceUtilization />, protected: true, roles: ['admin'] },
  { path: ROUTES.RESOURCE_UTIL_INSIGHTS, element: <ResourceUtilization />, protected: true, roles: ['admin'] },
  { path: ROUTES.AI_VALIDATION, element: <AIValidation />, protected: true, roles: ['admin'] },
  { path: ROUTES.ADMIN_DASHBOARD, element: <AdminDashboard />, protected: true, roles: ['admin'] },
  { path: ROUTES.ATTENDANCE_MANAGEMENT, element: <AttendanceManagement />, protected: true, roles: ['admin'] },
  { path: ROUTES.LEAVE_BALANCE, element: <LeaveBalance />, protected: true, roles: ['admin'] },
  { path: ROUTES.ADMIN_CHAT_HISTORY, element: <AdminChatHistory />, protected: true, roles: ['admin'] },
  { path: ROUTES.ADMIN_MESSAGE_LOGS, element: <AdminMessageLogs />, protected: true, roles: ['admin'] },
  { path: ROUTES.PROMOTIONAL_EMAILS, element: <PromotionalEmails />, protected: true, roles: ['admin'] },
  { path: ROUTES.ANNOUNCEMENTS_ADMIN, element: <AnnouncementManager />, protected: true, roles: ['admin'] },
  
  // Special routes
  { path: ROUTES.PENDING_APPROVAL, element: <PendingApprovalRoute />, special: true },
  { path: ROUTES.TWO_FA_SETUP, element: <TwoFactorSetupWrapper />, protected: true },
  { path: ROUTES.TWO_FA_SETUP_ONBOARDING, element: <TwoFactorSetupOnboardingWrapper />, protected: true, fullScreen: true },
  { path: ROUTES.ONBOARDING, element: <OnboardingFlowWrapper />, protected: true, fullScreen: true },
  
  // Root redirect
  { path: ROUTES.ROOT, element: <Navigate to={ROUTES.DASHBOARD} />, redirect: true },
];

// Helper function to generate routes
export const generateRoutes = () => {
  return routeConfig.map((route, index) => {
    const key = `route-${index}`;
    if (route.redirect) {
      return <Route key={key} path={route.path} element={route.element} />;
    }
    
    if (route.public) {
      return <Route key={key} path={route.path} element={route.element} />;
    }
    
    if (route.special) {
      return <Route key={key} path={route.path} element={route.element} />;
    }

    /* Full-screen routes (onboarding, 2FA setup) - no Layout, force light mode (no dark mode) */
    if (route.fullScreen) {
      return (
        <Route
          key={key}
          path={route.path}
          element={
            <ForceLightMode>
              <ProtectedRoute>
                {route.element}
              </ProtectedRoute>
            </ForceLightMode>
          }
        />
      );
    }
    
    if (route.roles) {
      return (
        <Route 
          key={key} 
          path={route.path} 
          element={
            <RoleProtectedRoute allowedRoles={route.roles}>
              <Layout>
                {route.element}
              </Layout>
            </RoleProtectedRoute>
          } 
        />
      );
    }
    
    return (
      <Route 
        key={key} 
        path={route.path} 
        element={
          <ProtectedRoute>
            <Layout>
              {route.element}
            </Layout>
          </ProtectedRoute>
        } 
      />
    );
  });
};
